import { message, Select, Switch } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import React, { useContext, useEffect, useState, useMemo } from "react";
import dashboardAndroid from "../../assets/dashboardAndroid.svg";
import dashboardApple from "../../assets/dashboardApple.svg";
import dashboardLogin from "../../assets/dashboardLogin.svg";
// import noChartDataFound from "../../assets/noChartDataFound.svg";
import downloadcsv from "../../assets/downloadcsv.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  analyticsAction,
  analyticsDataHandler,
} from "../../redux/action/analyticsSlice";
import { ThemeContext } from "../../common/themeContext";
import Loader from "../Loder/loader";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer as ResponsiveContainerBar,
  LabelList,
} from "recharts";
import GraphNoDataFound from "./GraphNoDataFound";
import { useCommonMessage } from "../../common/CommonMessage";
import { appDownloadDataHandler } from "../../redux/action/appDownloadSlice";

const Dashboard = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("last7Days");
  const { brandId } = useContext(ThemeContext); // Replace with dynamic value as needed
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isAlternate, setIsAlternate] = useState(false);
  const [isAlternateClaim, setIsAlternateClaim] = useState(false);
  // const eventTypes = brandId === 1 ? ["WELCOME_HC"] : ["WELCOME_PC"];

  const dateRangeOptions = [
    {
      value: "last7Days",
      title: "Last 7 Days",
      subtitle: "Previous 7 days",
    },
    {
      value: "thisMonth",
      title: "This Month",
      subtitle: "Current Month to date",
    },
    {
      value: "thisQuarter",
      title: "This Quarter",
      subtitle: "Current Quarter to date",
    },
    {
      value: "thisYear",
      title: "This Year",
      subtitle: "Current Year to date",
    },
    {
      value: "allTime",
      title: "All Time",
      subtitle: "Entire history",
    },
  ];

  const analyticsSelector = useSelector((state) => state?.analyticsDetails);
  const appDownloadSelector = useSelector((state) => state?.appDownloadDetails);
  const messageApi = useCommonMessage();
  useEffect(() => {
    if (analyticsSelector?.message) {
      messageApi.open({
        type: "error",
        content: analyticsSelector?.message,
      });
      dispatch(analyticsAction.analyticsInfoReset());
    }
  }, [analyticsSelector]);

  const getDataFromDay = (rangeKey) => {
    const now = new Date();
    let fromDay = undefined;
    if (rangeKey === "last7Days") {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      fromDay = d;
    } else if (rangeKey === "thisMonth") {
      fromDay = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (rangeKey === "thisQuarter") {
      const quarter = Math.floor(now.getMonth() / 3);
      fromDay = new Date(now.getFullYear(), quarter * 3, 1);
    } else if (rangeKey === "thisYear") {
      fromDay = new Date(now.getFullYear(), 0, 1);
    }
    if (fromDay) {
      // Format as DD-MM-YYYY
      const dd = String(fromDay.getDate()).padStart(2, "0");
      const mm = String(fromDay.getMonth() + 1).padStart(2, "0");
      const yyyy = fromDay.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    return undefined;
  };

  useEffect(() => {
    // Prevent API call if brandId is not set
    if (!brandId) return;

    const eventTypes = brandId === 1 ? ["WELCOME_HC"] : ["WELCOME_PC"];
    const payload = {
      brandId,
      eventTypes,
    };
    const dataFromDay = getDataFromDay(dateRange);
    if (dataFromDay) payload.dataFromDay = dataFromDay;
    dispatch(analyticsDataHandler(payload));
    dispatch(appDownloadDataHandler(payload));
  }, [brandId, dateRange]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDateRangeChange = (val) => {
    setDateRange(val);
  };

  const handleAlternateToggle = (checked) => {
    setIsAlternate(checked);
  };

  const handleAlternateClaimToggle = (checked) => {
    setIsAlternateClaim(checked);
  };

  const dashboardCards = [
    {
      icon: dashboardApple,
      title: "iOS App Downloads",
      value: appDownloadSelector?.data?.data?.appStoreCount ?? "0",
      alt: "iOS App Downloads",
    },
    {
      icon: dashboardAndroid,
      title: "Android App Downloads",
      value: appDownloadSelector?.data?.data?.googleAppCount ?? "0",
      alt: "Android App Downloads",
    },
    {
      icon: dashboardLogin,
      title: "Unique Logins (by Member ID)",
      value:
        analyticsSelector?.data?.data?.groupAnalyticsData?.totalUniqueUsers ??
        0,
      alt: "Unique Logins",
    },
  ];

  // Get API analytics
  const analytics = analyticsSelector?.data?.data?.emailAnalytics?.analytics;
  // Download CSV email data
  const analytics1 = analyticsSelector?.data?.data?.emailAnalytics?.rawData;
  // Download CSV claim data
  const claimAnalyticsData =
    analyticsSelector?.data?.data?.claimAnalyticsData?.rawData;

  const appEngagementData =
    analyticsSelector?.data?.data?.groupAnalyticsData?.rawData;

  const isEmpty = !analytics || analytics.length === 0;
  let emailAnalyticsData = !isEmpty && analytics[0] ? analytics[0] : {};
  const isBlankObject =
    Object.keys(emailAnalyticsData).length === 0 &&
    emailAnalyticsData.constructor === Object;

  // Email metric calculations (upgrade)
  const sent = Number(emailAnalyticsData?.sentCount ?? 0);
  const opened = Number(emailAnalyticsData?.openedCount ?? 0);
  const bounced = Number(emailAnalyticsData?.bounceCount ?? 0);
  const spam = Number(emailAnalyticsData?.complaintCount ?? 0);
  const notOpened = sent - (opened + bounced + spam);
  const openRate = sent > 0 ? (opened / sent) * 100 : 0;
  const bounceRate = sent > 0 ? (bounced / sent) * 100 : 0;
  const spamRate = sent > 0 ? (spam / sent) * 100 : 0;
  const notOpenedRate = sent > 0 ? (notOpened / sent) * 100 : 0;

  const emailRateData = [
    {
      label: "Emails Opened",
      value: emailAnalyticsData?.openedCount,
      color: "#15345A",
    },
    {
      label: "Emails Not Opened",
      value:
        emailAnalyticsData?.sentCount -
        (emailAnalyticsData?.openedCount +
          emailAnalyticsData?.bounceCount +
          emailAnalyticsData?.complaintCount),
      color: "#56B3E5",
    },
    {
      label: "Emails Bounced",
      value: emailAnalyticsData?.bounceCount,
      color: "#E95949",
    },
    {
      label: "Reported as SPAM",
      value: emailAnalyticsData?.complaintCount,
      color: "#2191D0",
    },
  ];

  const emailAnalytics = [
    {
      label: "Emails Opened",
      value: emailAnalyticsData.sentCount ?? 0,
      color: "#15345A",
    },
    {
      label: "Emails Not Opened",
      value: emailAnalyticsData.openedCount ?? 0,
      color: "#2191D0",
    },
    {
      label: "Emails Bounced",
      value: emailAnalyticsData.bounceCount ?? 0,
      color: "#56B3E5",
    },
    {
      label: "Reported as SPAM",
      value: emailAnalyticsData.complaintCount ?? 0,
      color: "#E95949",
    },
  ];
  // Define a color palette for dynamic groups
  const claimColors = [
    "#15345A", // dark blue
    "#2191D0", // blue
    "#56B3E5", // light blue
    "#C2E5F3", // pale blue
    "#E95949", // red
    "#3CC47C", // green -- add more as needed for extra groups
  ];

  const claimAnalyticsGroupClaims =
    analyticsSelector?.data?.data?.claimAnalyticsData?.groupClaims || {};
  const allClaimChartData = Object.values(claimAnalyticsGroupClaims).map(
    (group, idx) => {
      const totalClaims = group.totalPending + group.totalCompleted;
      const completionRate =
        totalClaims > 0 ? (group.totalCompleted / totalClaims) * 100 : 0;
      return {
        name: group.groupName,
        completionRate: completionRate, // decimal, not rounded
        pending: group.totalPending,
        completed: group.totalCompleted,
        total: totalClaims,
        color: claimColors[idx % claimColors.length],
      };
    }
  );

  // Sort and filter based on toggle (Top 15 or Bottom 15) for Claims
  // const claimChartData = useMemo(() => {
  //   const sorted = [...allClaimChartData].sort((a, b) =>
  //     isAlternateClaim ? a.completionRate - b.completionRate : b.completionRate - a.completionRate
  //   );
  //   return sorted.slice(0, 15);
  // }, [allClaimChartData, isAlternateClaim]);

  // Use for display of dynamic progress bars (instead of static appAnalytics)

  // const dynamicClaimBars = claimChartData.map((g) => ({
  //   label: g.name,
  //   value: g.completionRate.toFixed(2) + "%",
  //   percentage: g.completionRate,
  //   color: g.color,
  // }));
  // const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    index,
    payload,
    value,
  }) => {
    // Only show label when hovering this specific slice
    if (activePieIdx !== index) {
      return null;
    }

    if (!payload.label || percent === 0) return null;

    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Responsive positioning based on screen size
    const labelOffset = windowWidth <= 1550 ? 15 : 30;
    const horizontalExtension = windowWidth <= 1550 ? 15 : 22;
    const textPadding = windowWidth <= 1550 ? 8 : 12;

    // Position calculation (similar to Recharts example)
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + labelOffset) * cos;
    const my = cy + (outerRadius + labelOffset) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * horizontalExtension;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * textPadding}
          y={ey}
          textAnchor={textAnchor}
          fill={payload.color}
          style={{ fontSize: pieChartDimensions.fontSize, fontWeight: 600 }}
        >
          {payload.label}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * textPadding}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
          style={{ fontSize: pieChartDimensions.fontSize - 2 }}
        >
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };
  const renderLabelLine = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    index,
    payload,
  }) => {
    // Hide label line if value is 0
    if (payload.value === 0) {
      return null;
    }

    // Only show label line when hovering this specific slice
    if (activePieIdx !== index) {
      return null;
    }

    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Responsive positioning based on screen size
    const labelOffset = windowWidth <= 1550 ? 15 : 30;
    const horizontalExtension = windowWidth <= 1550 ? 15 : 22;

    // Three-point line: pie edge -> angled extension -> horizontal line
    // Start point: at the edge of the pie
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;

    // Middle point: extended outward
    const mx = cx + (outerRadius + labelOffset) * cos;
    const my = cy + (outerRadius + labelOffset) * sin;

    // End point: horizontal extension to label
    const ex = mx + (cos >= 0 ? 1 : -1) * horizontalExtension;
    const ey = my;

    return (
      <g>
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={payload.color}
          fill="none"
          strokeWidth={2}
        />
        <circle cx={ex} cy={ey} r={2} fill={payload.color} stroke="none" />
      </g>
    );
  };

  // Bar chart data for claim analytics
  // const claimAnalyticsGroupClaims = analyticsSelector?.data?.data?.claimAnalyticsData?.groupClaims || {};

  // const claimChartData = Object.values(claimAnalyticsGroupClaims).map(group => {
  //   const totalClaims = group.totalPending + group.totalCompleted;
  //   const completionRate = totalClaims > 0 ? Math.round((group.totalCompleted / totalClaims) * 100) : 0;
  //   return {
  //     name: group.groupName,
  //     completionRate,
  //     pending: group.totalPending,
  //     completed: group.totalCompleted,
  //     total: totalClaims
  //   };
  // });

  // New: Group Engagement Analytics
  const groupEngagements =
    analyticsSelector?.data?.data?.groupAnalyticsData?.groupEngagements || {};
  const totalUniqueUsersForEngagement =
    analyticsSelector?.data?.data?.groupAnalyticsData?.totalUniqueUsers || 1;

  const allGroupEngagementsData = Object.values(groupEngagements)?.map(
    (group, idx) => {
      const percentNum = (group.uniqueUsers / group.totalUser) * 100;
      return {
        name: group.groupName,
        uniqueUsers: group.uniqueUsers,
        totalUser: group.totalUser,
        percent: percentNum, // number, good for bar width
        percentText: percentNum.toFixed(1), // formatted for display
        color: claimColors[idx % claimColors.length],
      };
    }
  );
  // Sort and filter based on toggle (Top 15 or Bottom 15)
  // const groupEngagementsChartData = useMemo(() => {
  //   const sorted = [...allGroupEngagementsData].sort((a, b) =>
  //     isAlternate ? a.percent - b.percent : b.percent - a.percent
  //   );
  //   return sorted.slice(0, 15);
  // }, [allGroupEngagementsData, isAlternate]);

  const allEmailRatesZero = emailRateData.every((item) => !item.value);

  const pieChartData = allEmailRatesZero
    ? [{ label: "No Data Found", value: 1, color: "#e6e6e6" }]
    : emailRateData;

  const [activePieIdx, setActivePieIdx] = useState(null);

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
    } = props;

    // 3D-like pop-out offset
    const offset = 14;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const popoutCX = cx;
    const popoutCY = cy;

    return (
      <g>
        {/* Pop-out sector */}
        <Sector
          cx={popoutCX}
          cy={popoutCY}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          filter="url(#shadow)"
        />
      </g>
    );
  };

  // CSV Export Helper
  function exportToCSV(data, filename = "email_analytics.csv") {
    if (!Array.isArray(data) || data.length === 0) {
      message.error("No data to download");
      return;
    }
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers
        .map((field) =>
          row[field] !== null && row[field] !== undefined
            ? `"${String(row[field]).replace(/"/g, '""')}"`
            : ""
        )
        .join(",")
    );
    const csvContent = [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Responsive pie chart dimensions
  const pieChartDimensions = useMemo(() => {
    if (windowWidth <= 1550) {
      return {
        height: 350,
        innerRadius: 60,
        outerRadius: 120,
        margin: { top: 20, right: 80, bottom: 20, left: 80 },
        fontSize: 10,
      };
    }
    return {
      height: 450,
      innerRadius: 80,
      outerRadius: 160,
      margin: { top: 20, right: 100, bottom: 20, left: 100 },
      fontSize: 14,
    };
  }, [windowWidth]);

  const getSlices = (arr) => {
    const limit = 15;
    const first15 = arr.slice(0, limit);
    const last15 = arr.length > limit ? arr.slice(-limit) : arr;
    return {
      first15,
      last15,
    };
  };
  const EngagementBarChart = useMemo(
    () =>
      allGroupEngagementsData?.length > 0 ? (
        <div className="dashboardCard mb-40">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="fs-18 fw-medium">
              Count of App Engagement by Group
            </div>
            <div
              className="primarybtn d-flex align-items-center gap-1 fs-18 cursor-pointer"
              onClick={() =>
                exportToCSV(appEngagementData, "group_engagement.csv")
              }
            >
              <img src={downloadcsv} alt="downloadcsv" />
              Download CSV
            </div>
          </div>
          <div className="divider"></div>
          <div
            className={`d-grid  gap-3 mt-3 align-items-center  nooutline  ${
              dateRange === "allTime" || dateRange === "thisYear"
                ? "yearlygraph"
                : ""
            }`}
          >
            {/* <div className={`d-grid grid-2 gap-3 mt-3 align-items-center  nooutline  ${ dateRange === "allTime" || dateRange === "thisYear" ? "yearlygraph" : ""}`}> */}
            {allGroupEngagementsData?.length > 15 && (
              <div className="d-flex align-center justify-content-end gap-3 custom-toggle mb-24">
                <span
                  // className={`toggle-label fs-14`}
                  className={`toggle-label fs-14 ${!isAlternate ? "gm" : "o5"}`}
                >
                  Top 15
                </span>
                <Switch
                  checked={isAlternate}
                  onChange={handleAlternateToggle}
                  // className={`custom-switch`}
                  className={`custom-switch ${isAlternate ? "on" : "off"}`}
                />
                <span
                  // className={`toggle-label fs-14`}
                  className={`toggle-label fs-14 ${isAlternate ? "gm" : "o5"}`}
                >
                  Bottom 15
                </span>
              </div>
            )}
            <div style={{ width: "100%", height: "100%" }}>
              <ResponsiveContainerBar height={450}>
                <BarChart
                  // data = {isAlternate
                  // ? allGroupEngagementsData?.slice(-(allGroupEngagementsData.length - 15))
                  // : allGroupEngagementsData?.slice(0, 15)}
                  data={
                    isAlternate
                      ? getSlices(allGroupEngagementsData).last15
                      : getSlices(allGroupEngagementsData).first15
                  }
                  margin={{ top: 55, right: 32, left: 16, bottom: 80 }}
                  barCategoryGap={32}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-90}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 14 }}
                    tickFormatter={(value) =>
                      value?.length > 15
                        ? `${value.substring(0, 15)}...`
                        : value
                    }
                  />
                  <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <Bar
                    dataKey="percent"
                    name="Engaged Users"
                    maxBarSize={70}
                    activeBar={false}
                    radius={[8, 8, 0, 0]}
                  >
                    {allGroupEngagementsData.map((entry, idx) => (
                      <Cell
                        key={`cell-bar-engagement-${entry.name || idx}`}
                        fill={entry.color}
                      />
                    ))}
                    <LabelList
                      dataKey="percentText"
                      position="top"
                      angle={-90}
                      textAnchor="start"
                      fontSize={14}
                      formatter={(val) => `${val}%`}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainerBar>
            </div>
            {/* <div>
            <div className="productname mb-3">
              % of Total Engagement by Group Name
            </div>
            <div className={`d-flex flex-column gap-35 ${ dateRange === "allTime" || dateRange === "thisYear" ? "yearlygraphList" : ""}`}>
              {groupEngagementsChartData.map((item, index) => (
                <div className="d-flex align-items-center justify-content-between graphCard fs-16" key={`group-engagement-row-${item.name || index}`}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="graphColor graphColorCircle" style={{ backgroundColor: item.color }}></div>
                    <div className="">{item.name}</div>
                  </div>
                  <div className=" gb">{item.percentText}%</div>
                </div>
              ))}
            </div>
          </div> */}
          </div>
        </div>
      ) : (
        <GraphNoDataFound message="No Engagement Data Available" />
      ),
    [
      JSON.stringify(allGroupEngagementsData),
      appEngagementData,
      downloadcsv,
      isAlternate,
    ]
  );

  return (
    <>
      {(analyticsSelector?.isLoading || appDownloadSelector?.isLoading) && (
        <Loader loaderTransform="loaderTransform" />
      )}
      <div className="rightside">
        <div className="rightcontent">
          <div className="d-flex justify-content-between align-items-center mb-20">
            <p className="gm">Dashboard</p>
            <div
              className="noicon visibleicon pe-xxl-2 me-xxl-2 mb-xxl-0 selecttype mx-300"
              style={{ position: "relative" }}
            >
              <Select
                placeholder="Select date range"
                className="w-100 dateRangeSelect"
                value={dateRange}
                onChange={handleDateRangeChange}
                optionLabelProp="label"
              >
                {dateRangeOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.title}
                    className={
                      dateRange === option.value ? "selected-option" : ""
                    }
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="option-title">{option.title}</div>
                        <div className="option-subtitle">{option.subtitle}</div>
                      </div>
                      {dateRange === option.value && (
                        <CheckOutlined className="check-icon" />
                      )}
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="heedprodct mb-3">App Analytics Summary</div>
          <div className="d-grid grid-3 gap-3 mb-40">
            {dashboardCards.map((card, index) => (
              <div key={index} className="dashboardCard">
                <div className="dashboardCardIcon mb-25">
                  <img src={card.icon} alt={card.alt} />
                </div>
                <div className="productname mb-10">{card.title}</div>
                <div className="fs-28 gb">{card.value}</div>
              </div>
            ))}
          </div>
          <div className="heedprodct mb-3">Email Analytics</div>
          {allEmailRatesZero ? (
            <GraphNoDataFound message="No Email Analytics Data Available" />
          ) : (
            <>
              <div className="dashboardCard mb-40">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="fs-18 fw-medium">
                    Count of App Engagement by Group
                  </div>
                  <div
                    className="primarybtn d-flex align-items-center gap-1 fs-18 cursor-pointer"
                    onClick={() =>
                      exportToCSV(analytics1, "email_analytics.csv")
                    }
                  >
                    <img src={downloadcsv} alt="downloadcsv" />
                    Download CSV
                  </div>
                </div>
                <div className="divider"></div>

                <div className="d-grid grid-2 gap-3 mt-3 align-items-center justify-content-center">
                  <div
                    className="piechartcontainer nooutline"
                    style={{ position: "relative" }}
                  >
                    <div className="w-100">
                      <div className="fs-16">
                        Total Emails Sent:{" "}
                        <span className="gb">
                          {emailAnalyticsData?.sentCount}
                        </span>
                      </div>
                      <ResponsiveContainer
                        width="100%"
                        height={pieChartDimensions.height}
                      >
                        <PieChart margin={pieChartDimensions.margin}>
                          <defs>
                            <filter
                              id="shadow"
                              x="-50%"
                              y="-50%"
                              width="200%"
                              height="200%"
                            >
                              <feGaussianBlur
                                in="SourceAlpha"
                                stdDeviation="3"
                                result="blur"
                              />
                              <feOffset
                                in="blur"
                                dx="0"
                                dy="0"
                                result="offsetBlur"
                              />
                              <feMerge>
                                <feMergeNode />
                                <feMergeNode in="SourceGraphic" />
                              </feMerge>
                            </filter>
                          </defs>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={pieChartDimensions.innerRadius}
                            outerRadius={pieChartDimensions.outerRadius}
                            dataKey="value"
                            paddingAngle={0}
                            stroke="none"
                            activeIndex={activePieIdx}
                            activeShape={renderActiveShape}
                            onMouseEnter={(_, idx) => setActivePieIdx(idx)}
                            onMouseLeave={() => setActivePieIdx(null)}
                            labelLine={renderLabelLine}
                            label={
                              (!allEmailRatesZero && renderCustomizedLabel) ||
                              undefined
                            }
                            isAnimationActive={false}
                          >
                            {pieChartData.map((entry, idx) => (
                              <Cell
                                key={`cell-${idx}`}
                                fill={entry.color}
                                opacity={
                                  activePieIdx !== null && activePieIdx !== idx
                                    ? 0.5
                                    : 1
                                }
                              />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Always render all slice labels with absolute SVG overlay */}
                    {/* <svg width={350} height={350} style={{position: 'absolute', top: 0, left: "50%",transform: "translateX(-50%)", pointerEvents: 'none'}}>
                  {pieChartData.map((entry, idx) => {
                    // --- Same calculation as in renderCustomizedLabel ---
                    const sum = pieChartData.reduce((acc, cur) => acc + cur.value, 0);
                    if (!sum || entry.value === 0) return null;
                    let angleSum = 0, startAngle = 0, endAngle = 0, midAngle = 0;
                    for (let i = 0; i <= idx; ++i) {
                      let val = pieChartData[i].value;
                      let pct = val / sum;
                      if (i === 0) {
                        startAngle = 0;
                        endAngle = pct * 360;
                      } else {
                        startAngle = angleSum;
                        endAngle = angleSum + pct * 360;
                      }
                      if (i === idx) {
                        midAngle = (startAngle + endAngle) / 2;
                      }
                      angleSum += pct * 360;
                    }
                    const RADIAN = Math.PI / 180;
                    const innerRadius = 50, outerRadius = 125;
                    const radius = innerRadius + (outerRadius - innerRadius) / 2;
                    const x = 175 + radius * Math.cos(-midAngle * RADIAN);
                    const y = 175 + radius * Math.sin(-midAngle * RADIAN);
                    return (
                      <text
                        key={entry.label + idx}
                        x={x}
                        y={y}
                        fill="#fff"
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{ fontSize: 16, fontWeight: 700, filter: 'drop-shadow(2px 2px 4px #222)'}}
                      >
                        {((entry.value / sum) * 100).toFixed(0)}%
                      </text>
                    );
                  })}
                </svg> */}
                  </div>
                  <div>
                    <div className="productname mb-3">Breakdown</div>
                    <div className="d-flex flex-column gap-3">
                      {emailRateData.map((item, index) => (
                        <div
                          key={index}
                          className="d-flex align-items-center justify-content-between graphCard fs-16"
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="graphColor"
                              style={{ backgroundColor: item.color }}
                            ></div>
                            <div className="">{item.label}</div>
                          </div>
                          <div className=" gb">
                            {isNaN(item.value)
                              ? "0.00%"
                              : `${item.value.toFixed(0)}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="heedprodct mb-3">App Engagement</div>
          {EngagementBarChart}
          {brandId === 2 && (
            <>
              <div className="heedprodct mb-3">Claims</div>
              {claimAnalyticsData?.length > 0 ? (
                <>
                  <div className="dashboardCard mb-40">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="fs-18 fw-medium">
                        Member Attempted Claims by Group
                      </div>
                      <div
                        className="primarybtn d-flex align-items-center gap-1 fs-18 cursor-pointer"
                        onClick={() =>
                          exportToCSV(claimAnalyticsData, "claim_analytics.csv")
                        }
                      >
                        <img src={downloadcsv} alt="downloadcsv" />
                        Download CSV
                      </div>
                    </div>
                    <div className="divider"></div>
                    <div className="d-grid  gap-3 mt-3 nooutline align-items-center">
                      {allClaimChartData?.length > 15 && (
                        <div className="d-flex align-center justify-content-end gap-3 custom-toggle mb-24">
                          <span
                            // className={`toggle-label fs-14`}
                            className={`toggle-label fs-14 ${
                              !isAlternateClaim ? "gm" : "o5"
                            }`}
                          >
                            Total pending
                          </span>
                          <Switch
                            checked={isAlternateClaim}
                            onChange={handleAlternateClaimToggle}
                            // className={`custom-switch`}
                            className={`custom-switch ${
                              isAlternateClaim ? "on" : "off"
                            }`}
                          />
                          <span
                            // className={`toggle-label fs-14`}
                            className={`toggle-label fs-14 ${
                              isAlternateClaim ? "gm" : "o5"
                            }`}
                          >
                            {/* Bottom 15 */}
                            Total completed
                          </span>
                        </div>
                      )}
                      <div>
                        <div style={{ width: "100%" }}>
                          <ResponsiveContainer width="100%" height={450}>
                            <BarChart
                              data={
                                isAlternateClaim
                                  ? getSlices(allClaimChartData).last15
                                  : getSlices(allClaimChartData).first15
                              }
                              margin={{
                                top: 55,
                                right: 32,
                                left: 16,
                                bottom: 80,
                              }}
                              barCategoryGap={32}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis
                                dataKey="name"
                                angle={-90}
                                textAnchor="end"
                                height={80}
                                tick={{ fontSize: 14 }}
                                tickFormatter={(value) =>
                                  value?.length > 15
                                    ? `${value.substring(0, 15)}...`
                                    : value
                                }
                              />
                              <YAxis
                                domain={[0, 100]}
                                tickFormatter={(v) => `${v}%`}
                              />
                              {/* <Tooltip content={renderClaimTooltip} cursor={false}/> */}
                              <Bar
                                dataKey="completionRate"
                                maxBarSize={70}
                                activeBar={false}
                                radius={[8, 8, 0, 0]}
                              >
                                {allClaimChartData?.map((entry, idx) => (
                                  <Cell
                                    key={`cell-bar-${idx}`}
                                    fill={entry.color}
                                  />
                                ))}
                                <LabelList
                                  dataKey="completionRate"
                                  position="top"
                                  angle={-90}
                                  textAnchor="start"
                                  fontSize={14}
                                  formatter={(val) => `${val.toFixed(2)}%`}
                                />
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                      {/* <div>
                        <div className="productname mb-3">
                          % of Claims Completed by Group
                        </div>
                        <div className="d-flex flex-column gap-35">
                          {dynamicClaimBars.map((item, index) => (
                            <div key={index}>
                              <div className="d-flex align-items-center justify-content-between mb-3 fs-16">
                                <div className="d-flex align-items-center gap-3">
                                  <div
                                    className="graphColor graphColorCircle"
                                    style={{ backgroundColor: item.color }}
                                  ></div>
                                  <div className="">{item.label}</div>
                                </div>
                                <div className="textgrey">{item.value}</div>
                              </div>
                              <div className="engagement-progress-bar">
                                <div
                                  className="engagement-progress-fill"
                                  style={{
                                    width: `${item.percentage}%`,
                                    backgroundColor: item.color,
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div> */}
                    </div>
                  </div>
                </>
              ) : (
                <GraphNoDataFound message="No Claims Data Available" />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
// Tooltip for bar chart
const renderClaimTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const { completed, pending } = payload[0].payload;
    const total = completed + pending;
    const percent = total > 0 ? ((completed / total) * 100).toFixed(2) : "0.00";
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #d1d1d1",
          borderRadius: 4,
          padding: 10,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>{label}</div>
        <div>
          Completed: <b>{completed}</b>
        </div>
        <div>
          Pending: <b>{pending}</b>
        </div>
        <div>
          Completion Rate: <b>{percent}%</b>
        </div>
      </div>
    );
  }
  return null;
};
